/**
 * Kind-generic item UI for the vault overview — one editor + one display card
 * that cover all four v1 item kinds (login / note / card / identity). The crypto
 * and client layers are already kind-agnostic (`encryptItemPayload` JSON-encodes
 * any payload; `createItem`/`updateItem` take an `ItemKind`), so this module is
 * purely presentational: it maps a typed `ItemPayload` ⇄ flat form fields, and
 * renders per-kind summaries + reveal/copy for the sensitive fields.
 *
 * Everything here runs on already-decrypted payloads held in tab memory while
 * unlocked; nothing crosses the network in plaintext.
 */
import type { FormEvent, ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type {
  CardPayload,
  IdentityPayload,
  ItemKind,
  ItemPayload,
  LoginPayload,
  NotePayload,
} from '@heivol-locker/shared-types';
import { Button, Card, Field, Input, Textarea } from './ui';

/** Copy-to-clipboard with an auto-wipe after 20s, tracking which item was copied. */
const CLIPBOARD_CLEAR_MS = 20_000;
export function useClipboardCopy(): {
  copiedId: string | null;
  copy: (id: string, value: string) => void;
} {
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => {
    return () => {
      if (timer.current !== undefined) {
        window.clearTimeout(timer.current);
      }
    };
  }, []);
  const copy = useCallback((id: string, value: string): void => {
    void navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopiedId(id);
        if (timer.current !== undefined) {
          window.clearTimeout(timer.current);
        }
        timer.current = window.setTimeout(() => {
          void navigator.clipboard.writeText('').catch(() => undefined);
          setCopiedId((c) => (c === id ? null : c));
          timer.current = undefined;
        }, CLIPBOARD_CLEAR_MS);
      })
      .catch(() => undefined);
  }, []);
  return { copiedId, copy };
}

export interface ItemView {
  id: string;
  version: number;
  payload: ItemPayload;
}

export const ITEM_KINDS: readonly ItemKind[] = ['login', 'note', 'card', 'identity'];

export const KIND_LABELS: Record<ItemKind, string> = {
  login: 'Login',
  note: 'Secure note',
  card: 'Card',
  identity: 'Identity',
};

const PW_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%^&*';

export function generatePassword(length = 20): string {
  const bytes = crypto.getRandomValues(new Uint8Array(length));
  let out = '';
  for (let i = 0; i < length; i += 1) {
    out += PW_ALPHABET.charAt((bytes[i] as number) % PW_ALPHABET.length);
  }
  return out;
}

/** Flat superset of every kind's editable fields — one useState, no conditional hooks. */
interface Draft {
  title: string;
  // login
  url: string;
  username: string;
  password: string;
  // note (also login/card notes)
  body: string;
  // card
  cardholder: string;
  number: string;
  expiry: string;
  cvv: string;
  // identity
  fullName: string;
  address: string;
  phone: string;
  email: string;
}

const EMPTY_DRAFT: Draft = {
  title: '',
  url: '',
  username: '',
  password: '',
  body: '',
  cardholder: '',
  number: '',
  expiry: '',
  cvv: '',
  fullName: '',
  address: '',
  phone: '',
  email: '',
};

function draftFromPayload(p: ItemPayload): Draft {
  const d: Draft = { ...EMPTY_DRAFT, title: p.title };
  switch (p.kind) {
    case 'login':
      return { ...d, url: p.urls[0] ?? '', username: p.username, password: p.password };
    case 'note':
      return { ...d, body: p.body };
    case 'card':
      return {
        ...d,
        cardholder: p.cardholder,
        number: p.number,
        expiry: p.expiry,
        cvv: p.cvv,
      };
    case 'identity':
      return {
        ...d,
        fullName: p.fullName,
        address: p.address ?? '',
        phone: p.phone ?? '',
        email: p.email ?? '',
      };
  }
}

/** Build a normalised, typed payload for `kind` from the flat draft. */
export function payloadFromDraft(kind: ItemKind, d: Draft): ItemPayload {
  const title = d.title.trim();
  switch (kind) {
    case 'login': {
      const p: LoginPayload = {
        kind: 'login',
        title,
        urls: d.url.trim().length > 0 ? [d.url.trim()] : [],
        username: d.username.trim(),
        password: d.password,
      };
      return p;
    }
    case 'note': {
      const p: NotePayload = { kind: 'note', title, body: d.body };
      return p;
    }
    case 'card': {
      const p: CardPayload = {
        kind: 'card',
        title,
        cardholder: d.cardholder.trim(),
        number: d.number.replace(/\s+/g, ''),
        expiry: d.expiry.trim(),
        cvv: d.cvv.trim(),
      };
      return p;
    }
    case 'identity': {
      // `exactOptionalPropertyTypes` forbids `key: undefined` — omit empty optionals entirely.
      const address = d.address.trim();
      const phone = d.phone.trim();
      const email = d.email.trim();
      const p: IdentityPayload = {
        kind: 'identity',
        title,
        fullName: d.fullName.trim(),
        ...(address.length > 0 ? { address } : {}),
        ...(phone.length > 0 ? { phone } : {}),
        ...(email.length > 0 ? { email } : {}),
      };
      return p;
    }
  }
}

/** Kind-aware, case-insensitive match over the fields a user would search by. */
export function itemMatches(p: ItemPayload, q: string): boolean {
  const hay: string[] = [p.title];
  switch (p.kind) {
    case 'login':
      hay.push(p.username, ...p.urls);
      break;
    case 'note':
      hay.push(p.body);
      break;
    case 'card':
      hay.push(p.cardholder);
      break;
    case 'identity':
      hay.push(p.fullName, p.email ?? '', p.phone ?? '');
      break;
  }
  return hay.some((s) => s.toLowerCase().includes(q));
}

/** Non-title subtitle shown under the item title in the list. */
function subtitle(p: ItemPayload): string {
  switch (p.kind) {
    case 'login':
      return [p.username || '—', p.urls[0]].filter(Boolean).join(' · ');
    case 'note':
      return p.body.split('\n')[0]?.slice(0, 80) || '—';
    case 'card':
      return [p.cardholder || '—', p.number ? `•••• ${p.number.slice(-4)}` : '']
        .filter(Boolean)
        .join(' · ');
    case 'identity':
      return [p.fullName || '—', p.email].filter(Boolean).join(' · ');
  }
}

/** The single value the Copy button copies, or null when there is nothing obvious to copy. */
function copyValue(p: ItemPayload): string | null {
  switch (p.kind) {
    case 'login':
      return p.password;
    case 'card':
      return p.number;
    case 'note':
      return p.body;
    case 'identity':
      return null;
  }
}

/**
 * Kind-aware field editor. `kind` is fixed for the lifetime of the form (the
 * parent remounts via `key` to switch kind or reset). Owns its own draft state.
 */
export function ItemForm({
  kind,
  initial,
  submitLabel,
  busyLabel,
  busy,
  onSubmit,
  onCancel,
}: {
  kind: ItemKind;
  initial?: ItemPayload;
  submitLabel: string;
  busyLabel: string;
  busy: boolean;
  onSubmit: (payload: ItemPayload) => void;
  onCancel?: () => void;
}) {
  const [draft, setDraft] = useState<Draft>(() =>
    initial !== undefined ? draftFromPayload(initial) : EMPTY_DRAFT,
  );
  const set = (patch: Partial<Draft>): void => setDraft((d) => ({ ...d, ...patch }));

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    if (draft.title.trim().length === 0) {
      return;
    }
    onSubmit(payloadFromDraft(kind, draft));
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Title">
          <Input
            value={draft.title}
            onChange={(e) => set({ title: e.target.value })}
            placeholder={kind === 'login' ? 'GitHub' : KIND_LABELS[kind]}
          />
        </Field>

        {kind === 'login' && (
          <>
            <Field label="Website">
              <Input
                value={draft.url}
                onChange={(e) => set({ url: e.target.value })}
                placeholder="github.com"
                inputMode="url"
              />
            </Field>
            <Field label="Username">
              <Input value={draft.username} onChange={(e) => set({ username: e.target.value })} />
            </Field>
            <Field label="Password">
              <div className="flex gap-2">
                <Input
                  value={draft.password}
                  onChange={(e) => set({ password: e.target.value })}
                  type="text"
                  className="font-mono"
                />
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => set({ password: generatePassword() })}
                  title="Generate a strong password"
                >
                  Generate
                </Button>
              </div>
            </Field>
          </>
        )}

        {kind === 'card' && (
          <>
            <Field label="Cardholder">
              <Input
                value={draft.cardholder}
                onChange={(e) => set({ cardholder: e.target.value })}
              />
            </Field>
            <Field label="Card number">
              <Input
                value={draft.number}
                onChange={(e) => set({ number: e.target.value })}
                inputMode="numeric"
                autoComplete="off"
                className="font-mono"
              />
            </Field>
            <Field label="Expiry (MM/YY)">
              <Input
                value={draft.expiry}
                onChange={(e) => set({ expiry: e.target.value })}
                placeholder="04/28"
                autoComplete="off"
              />
            </Field>
            <Field label="CVV">
              <Input
                value={draft.cvv}
                onChange={(e) => set({ cvv: e.target.value })}
                inputMode="numeric"
                autoComplete="off"
                className="font-mono"
              />
            </Field>
          </>
        )}

        {kind === 'identity' && (
          <>
            <Field label="Full name">
              <Input value={draft.fullName} onChange={(e) => set({ fullName: e.target.value })} />
            </Field>
            <Field label="Email">
              <Input
                value={draft.email}
                onChange={(e) => set({ email: e.target.value })}
                inputMode="email"
              />
            </Field>
            <Field label="Phone">
              <Input
                value={draft.phone}
                onChange={(e) => set({ phone: e.target.value })}
                inputMode="tel"
              />
            </Field>
            <Field label="Address">
              <Input value={draft.address} onChange={(e) => set({ address: e.target.value })} />
            </Field>
          </>
        )}
      </div>

      {kind === 'note' && (
        <Field label="Note">
          <Textarea
            value={draft.body}
            onChange={(e) => set({ body: e.target.value })}
            rows={5}
            placeholder="Anything you want encrypted…"
          />
        </Field>
      )}

      <div className="flex gap-2">
        <Button type="submit" disabled={busy || draft.title.trim().length === 0}>
          {busy ? busyLabel : submitLabel}
        </Button>
        {onCancel !== undefined && (
          <Button type="button" variant="ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
}

/** The revealable/secret area of a card, shown only for kinds that have a secret. */
function SecretArea({ payload, show }: { payload: ItemPayload; show: boolean }): ReactNode {
  const dot = '••••••••••••';
  switch (payload.kind) {
    case 'login':
      return <p className="mt-1 font-mono text-sm text-text">{show ? payload.password : dot}</p>;
    case 'card':
      return (
        <div className="mt-1 space-y-0.5 font-mono text-sm text-text">
          <p>{show ? payload.number : dot}</p>
          <p className="text-text-secondary">
            {show ? `exp ${payload.expiry} · cvv ${payload.cvv}` : 'exp •• · cvv •••'}
          </p>
        </div>
      );
    case 'note':
      return show ? (
        <p className="mt-1 whitespace-pre-wrap text-sm text-text">{payload.body || '—'}</p>
      ) : null;
    case 'identity':
      return show ? (
        <div className="mt-1 space-y-0.5 text-sm text-text">
          <p>{payload.fullName || '—'}</p>
          {payload.email !== undefined && <p className="text-text-secondary">{payload.email}</p>}
          {payload.phone !== undefined && <p className="text-text-secondary">{payload.phone}</p>}
          {payload.address !== undefined && (
            <p className="text-text-secondary">{payload.address}</p>
          )}
        </div>
      ) : null;
  }
}

/**
 * Per-kind line-art badge. Each glyph sizes to the surrounding font (1em) and
 * inherits text colour via `currentColor`, so it adapts to light/dark and to the
 * row's font size (list rows render it larger via `text-lg`).
 */
const badge = (d: string): ReactNode => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="inline-block h-[1em] w-[1em] shrink-0 align-[-0.125em]"
  >
    <path
      d={d}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const KIND_BADGE: Record<ItemKind, ReactNode> = {
  login: badge(
    'M17 8.99994C17 8.48812 16.8047 7.9763 16.4142 7.58579C16.0237 7.19526 15.5118 7 15 7M15 15C18.3137 15 21 12.3137 21 9C21 5.68629 18.3137 3 15 3C11.6863 3 9 5.68629 9 9C9 9.27368 9.01832 9.54308 9.05381 9.80704C9.11218 10.2412 9.14136 10.4583 9.12172 10.5956C9.10125 10.7387 9.0752 10.8157 9.00469 10.9419C8.937 11.063 8.81771 11.1823 8.57913 11.4209L3.46863 16.5314C3.29568 16.7043 3.2092 16.7908 3.14736 16.8917C3.09253 16.9812 3.05213 17.0787 3.02763 17.1808C3 17.2959 3 17.4182 3 17.6627V19.4C3 19.9601 3 20.2401 3.10899 20.454C3.20487 20.6422 3.35785 20.7951 3.54601 20.891C3.75992 21 4.03995 21 4.6 21H7V19H9V17H11L12.5791 15.4209C12.8177 15.1823 12.937 15.063 13.0581 14.9953C13.1843 14.9248 13.2613 14.8987 13.4044 14.8783C13.5417 14.8586 13.7588 14.8878 14.193 14.9462C14.4569 14.9817 14.7263 15 15 15Z',
  ),
  note: badge(
    'M13 3.5V6.2C13 7.88016 13 8.72024 13.327 9.36197C13.6146 9.92646 14.0735 10.3854 14.638 10.673C15.2798 11 16.1198 11 17.8 11H20.5M21 12.9882V16.2C21 17.8802 21 18.7202 20.673 19.362C20.3854 19.9265 19.9265 20.3854 19.362 20.673C18.7202 21 17.8802 21 16.2 21H7.8C6.11984 21 5.27976 21 4.63803 20.673C4.07354 20.3854 3.6146 19.9265 3.32698 19.362C3 18.7202 3 17.8802 3 16.2V7.8C3 6.11984 3 5.27976 3.32698 4.63803C3.6146 4.07354 4.07354 3.6146 4.63803 3.32698C5.27976 3 6.11984 3 7.8 3H11.0118C11.7455 3 12.1124 3 12.4577 3.08289C12.7638 3.15638 13.0564 3.27759 13.3249 3.44208C13.6276 3.6276 13.887 3.88703 14.4059 4.40589L19.5941 9.59411C20.113 10.113 20.3724 10.3724 20.5579 10.6751C20.7224 10.9436 20.8436 11.2362 20.9171 11.5423C21 11.8876 21 12.2545 21 12.9882Z',
  ),
  card: badge(
    'M22 10H2M2 8.2L2 15.8C2 16.9201 2 17.4802 2.21799 17.908C2.40973 18.2843 2.71569 18.5903 3.09202 18.782C3.51984 19 4.07989 19 5.2 19L18.8 19C19.9201 19 20.4802 19 20.908 18.782C21.2843 18.5903 21.5903 18.2843 21.782 17.908C22 17.4802 22 16.9201 22 15.8V8.2C22 7.0799 22 6.51984 21.782 6.09202C21.5903 5.7157 21.2843 5.40974 20.908 5.21799C20.4802 5 19.9201 5 18.8 5L5.2 5C4.0799 5 3.51984 5 3.09202 5.21799C2.7157 5.40973 2.40973 5.71569 2.21799 6.09202C2 6.51984 2 7.07989 2 8.2Z',
  ),
  identity: badge(
    'M20 21C20 19.6044 20 18.9067 19.8278 18.3389C19.44 17.0605 18.4395 16.06 17.1611 15.6722C16.5933 15.5 15.8956 15.5 14.5 15.5H9.5C8.10444 15.5 7.40665 15.5 6.83886 15.6722C5.56045 16.06 4.56004 17.0605 4.17224 18.3389C4 18.9067 4 19.6044 4 21M16.5 7.5C16.5 9.98528 14.4853 12 12 12C9.51472 12 7.5 9.98528 7.5 7.5C7.5 5.01472 9.51472 3 12 3C14.4853 3 16.5 5.01472 16.5 7.5Z',
  ),
};

/** One item row: display mode with reveal/copy/edit/delete, or inline edit mode. */
export function ItemCard({
  item,
  onSave,
  onDelete,
  onCopy,
  copied,
}: {
  item: ItemView;
  onSave: (id: string, version: number, payload: ItemPayload) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onCopy: (id: string, value: string) => void;
  copied: boolean;
}) {
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);

  const p = item.payload;
  const secret = copyValue(p);

  if (editing) {
    return (
      <Card className="space-y-3 p-4">
        <h3 className="text-sm font-semibold text-text">
          Edit {KIND_LABELS[p.kind].toLowerCase()}
        </h3>
        <ItemForm
          kind={p.kind}
          initial={p}
          submitLabel="Save changes"
          busyLabel="Saving…"
          busy={busy}
          onCancel={() => setEditing(false)}
          onSubmit={(payload) => {
            setBusy(true);
            void onSave(item.id, item.version, payload)
              .then(() => setEditing(false))
              .catch(() => undefined) // onSave surfaces its own error; keep form open
              .finally(() => setBusy(false));
          }}
        />
      </Card>
    );
  }

  return (
    <Card className="flex items-center justify-between gap-4 p-4">
      <div className="min-w-0">
        <p className="truncate font-medium text-text">
          <span aria-hidden className="mr-1.5">
            {KIND_BADGE[p.kind]}
          </span>
          {p.title}
        </p>
        <p className="truncate text-sm text-text-secondary">{subtitle(p)}</p>
        <SecretArea payload={p} show={show} />
      </div>
      <div className="flex shrink-0 flex-wrap justify-end gap-2">
        <Button
          variant="ghost"
          onClick={() => setShow((s) => !s)}
          aria-label={show ? 'Hide details' : 'Reveal details'}
        >
          {show ? 'Hide' : 'Reveal'}
        </Button>
        {secret !== null && (
          <Button variant="ghost" onClick={() => onCopy(item.id, secret)}>
            {copied ? 'Copied ✓' : 'Copy'}
          </Button>
        )}
        <Button variant="ghost" onClick={() => setEditing(true)}>
          Edit
        </Button>
        {confirmDelete ? (
          <>
            <Button
              variant="danger"
              disabled={busy}
              onClick={() => {
                setBusy(true);
                void onDelete(item.id)
                  .catch(() => undefined)
                  .finally(() => setBusy(false));
              }}
            >
              {busy ? 'Deleting…' : 'Confirm'}
            </Button>
            <Button variant="ghost" disabled={busy} onClick={() => setConfirmDelete(false)}>
              Cancel
            </Button>
          </>
        ) : (
          <Button variant="ghost" onClick={() => setConfirmDelete(true)} aria-label="Delete item">
            Delete
          </Button>
        )}
      </div>
    </Card>
  );
}

/**
 * Compact list row that links to the item's detail route. The whole title area
 * is a link; a quick Copy stays inline for the one high-frequency action (full
 * reveal/edit/delete live on the detail page). Secrets are never shown here.
 */
export function ItemRow({
  item,
  to,
  onCopy,
  copied,
}: {
  item: ItemView;
  to: string;
  onCopy: (id: string, value: string) => void;
  copied: boolean;
}) {
  const p = item.payload;
  const secret = copyValue(p);
  return (
    <Card className="flex items-center justify-between gap-3 p-4">
      <Link
        to={to}
        className="flex min-w-0 flex-1 items-center gap-3 rounded-md focus-visible:shadow-focus"
      >
        <span aria-hidden className="text-lg">
          {KIND_BADGE[p.kind]}
        </span>
        <span className="min-w-0">
          <span className="block truncate font-medium text-text">{p.title}</span>
          <span className="block truncate text-sm text-text-secondary">{subtitle(p)}</span>
        </span>
      </Link>
      <div className="flex shrink-0 items-center gap-2">
        {secret !== null && (
          <Button variant="ghost" onClick={() => onCopy(item.id, secret)}>
            {copied ? 'Copied ✓' : 'Copy'}
          </Button>
        )}
        <Link to={to} aria-label={`Open ${p.title}`}>
          <Button variant="ghost" tabIndex={-1}>
            Open ›
          </Button>
        </Link>
      </div>
    </Card>
  );
}
